/**
 * Appointment grouping indicator for order tables.
 *
 * Computes row spans for consecutive orders sharing the same appointment,
 * and renders a calendar icon with a hover card showing appointment details.
 */
import { CalendarDays } from 'lucide-react'

import type { OrderWithRelations } from '@snootsvet/shared'
import { APPOINTMENT_TYPES_BY_ID, formatName } from '@snootsvet/shared'

import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { TableCell } from '@/frontend/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'

/**
 * For each order, returns the rowSpan if it's the first in an appointment group,
 * or 0 if the cell should be skipped (covered by a previous rowSpan).
 * Orders without an appointment get rowSpan 1 (empty cell, no icon).
 */
export function computeAppointmentRowSpans(orders: OrderWithRelations[]): number[] {
  const spans: number[] = new Array(orders.length).fill(0)

  let i = 0
  while (i < orders.length) {
    const appointmentId = orders[i].consultation?.appointment?.id
    if (!appointmentId) {
      spans[i] = 1
      i++
      continue
    }

    // Count consecutive orders with the same appointment
    let groupSize = 1
    while (
      i + groupSize < orders.length &&
      orders[i + groupSize].consultation?.appointment?.id === appointmentId
    ) {
      groupSize++
    }

    spans[i] = groupSize
    // Remaining rows in group stay 0 (skip)
    i += groupSize
  }

  return spans
}

interface AppointmentGroupCellProps {
  order: OrderWithRelations
  rowSpan: number
}

/** Renders the grouped appointment icon cell, or an empty cell for non-appointment orders. */
export function AppointmentGroupCell({ order, rowSpan }: AppointmentGroupCellProps) {
  const appointment = order.consultation?.appointment
  if (!appointment) {
    return <TableCell className="w-6" />
  }

  const typeName = appointment.appointmentTypeId
    ? (APPOINTMENT_TYPES_BY_ID[appointment.appointmentTypeId]?.name ?? null)
    : null
  const staff = order.consultation?.staff
  const staffName = staff ? formatName(staff) : null

  return (
    <TableCell className="w-8 text-center align-middle" rowSpan={rowSpan}>
      <Tooltip>
        <TooltipTrigger className="inline-flex translate-y-px text-muted-foreground">
          <CalendarDays className="size-4" />
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-64">
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{typeName ?? 'Appointment'}</span>
            <span>
              <FormattedDateTime value={appointment.startTime} clinicId={appointment.clinicId} />
            </span>
            {staffName && <span>Vet: {staffName}</span>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TableCell>
  )
}
